import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomCheckbox from '@app/components/UI/Forms/CustomCheckbox/CustomCheckbox';
import CustomInput from '@app/components/UI/Forms/CustomInput/CustomInput';
import PasswordShowHide from '@app/components/UI/Forms/PasswordShowHide/PasswordShowHide';
import styles from '@app/components/UI/Login/LoginForm.module.scss';
import RecoverForm from '@app/components/UI/Login/RecoverForm/RecoverForm';
import CloseSVG from '@app/images/icons/CloseSVG';
import lockIcon from '@app/images/icons/lock.svg';
import mailIcon from '@app/images/icons/mail.svg';
import { ApplicationState } from '@app/store';
import * as LoginState from '@app/store/Login';

interface LoginFormProps {
	onChangeModalVisible: (e: boolean) => void;
}

const validationSchema = Yup.object({
	email: Yup.string().email('Value must be a valid Email address').required('Email is a required field'),
	password: Yup.string().required('Password is a required field'),
});

const LoginForm: React.FC<LoginFormProps> = ({ onChangeModalVisible }) => {
	const dispatch = useDispatch();
	const actions = bindActionCreators(LoginState.getActionCreators(), dispatch);
	const login = useSelector((state: ApplicationState) => state.login, shallowEqual);
	const isLoadingLogin = useSelector((state: ApplicationState) => state.login.isLoading, shallowEqual);

	const [checked, setChecked] = React.useState(false);
	const [showRecover, setShowRecover] = React.useState<boolean>(false);

	const closeModal = () => {
		onChangeModalVisible(false);
	};

	return (
		<div className={styles.container}>
			<CloseSVG className={styles.btnClose} onClick={closeModal} />

			{!showRecover ? (
				<>
					<h1 className="h2">
						Sign in
					</h1>
					<p className="h4 text-center">
						Sign in to track your pet’s quality of life.
					</p>
					<Formik
						onSubmit={(values) => {
							actions.login(values.email, values.password, 'auth', checked ?? false);
						}}
						initialValues={{
							email: '',
							password: '',
						}}
						validationSchema={validationSchema}
					>
						{(formikBag: FormikProps<any>) => {
							return (
								<Form className="h4">
									{!isLoadingLogin ? (
										<>
											<div className={styles.fields}>
												<CustomInput
													name="email"
													type="email"
													placeholder="Email Address"
													formikBag={formikBag}
													prefixIcon={mailIcon}
												/>
												<CustomInput
													name="password"
													placeholder="Password"
													type="password"
													formikBag={formikBag}
													prefixIcon={lockIcon}
													component={PasswordShowHide}
												/>
											</div>

											<div className={styles.flex}>
												<div>
													<CustomCheckbox
														onChangeChecked={setChecked}
														formikBag={formikBag}
														capture="Keep me signed in"
													/>
												</div>
												<div className={styles.forgot}>
													<a
														href="#"
														onClick={(e) => {
															e.preventDefault();
															setShowRecover(true);
														}}
													>
														Forgot your password?
													</a>
												</div>
											</div>

											<div className={styles.btnsWrap}>
												<button
													type="submit"
													className="btn btn-primary btn-md"
												>
													Sign in
												</button>
											</div>

											{login.message && (
												<p className={styles.errorMessage}>
													{login.message}
												</p>
											)}

											<div className={styles.registerAccount}>
												Don’t have an account?
												{' '}
												<NavLink to="/create-profile" onClick={closeModal}>
													Register now
												</NavLink>
											</div>
										</>
									) : <BounceLoader />}
								</Form>
							);
						}}
					</Formik>
				</>
			) : (
				<RecoverForm />
			)}
		</div>
	);
};

export default LoginForm;
